export type BreakdownView = 'department' | 'discipline' | 'phase' | 'tag';

/**
 * Which clickable value in a breakdown row was used to get to the task list — one of the three
 * count columns, one of the Progress bar's five segments, or the group name itself ('all').
 *
 * The values double as the drill-down endpoint's `metric` parameter. It matches on letters and
 * digits only, ignoring case, so these camelCase names reach it as `pastduemilestones` and
 * friends without any translation on the way out.
 */
export type BreakdownMetric =
    | 'all'
    | 'pastDueMilestones'
    | 'upcomingDeadlines'
    | 'unassignedWork'
    | 'complete'
    | 'pastDue'
    | 'inProgress'
    | 'notDue'
    | 'notApplicable';

/**
 * How each metric is named back to the user in the sidebar. The column metrics repeat their
 * column header and the segment metrics repeat their status label (GRAPH_STATUSES), so the
 * sidebar always names the thing that was clicked in the words it was clicked under.
 */
export const BREAKDOWN_METRIC_LABELS: Record<BreakdownMetric, string> = {
    all: 'All tasks',
    pastDueMilestones: 'Past-Due Milestones',
    upcomingDeadlines: 'Upcoming Deadlines',
    unassignedWork: 'Unassigned Work',
    complete: 'Complete',
    pastDue: 'Past Due',
    inProgress: 'In Progress',
    notDue: 'Not Due',
    notApplicable: 'Not Applicable',
};

/**
 * Identifies the breakdown row a user drilled into, and lives here rather than in
 * LocationBreakdownPanel so the panel, the table and Openings can all name it without
 * importing each other in a circle.
 *
 * Openings holds one of these (or null). Null is the normal case — a location row was
 * clicked — and is what tells the sidebar to show the plain location header instead of the
 * breadcrumb.
 */
export interface BreakdownSelection {
    locationId: number;
    locationKey: string;
    locationName: string;
    /** Which grouping the row came from, so the task query is scoped to it. */
    view: BreakdownView;
    groupId: number;
    groupName: string;
    /** Which value in the row was clicked. The group name itself drills in as 'all'. */
    metric: BreakdownMetric;
}
